import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const TESTIMONIALS = [
  {
    name: 'Ryan & Megan T.',
    neighborhood: 'Saxony, Fishers',
    text: 'We thought our Roche benefits were enough. Benjamin ran the numbers and showed us we were $600,000 short. Fixed it in one call for $34/month. Wish we had done this years ago.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Josh M.',
    neighborhood: 'Nickel Plate District, Fishers',
    text: 'I work at Launch Fishers and had zero life insurance. Benjamin got me $500,000 of coverage for less than my Netflix subscription. Took 20 minutes total.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Kayla & Derek S.',
    neighborhood: 'Geist area, Fishers',
    text: 'We bought our dream home near Geist Reservoir and realized we needed mortgage protection immediately. Benjamin had us covered in 48 hours. No drama, no hard sell.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Amanda L.',
    neighborhood: 'Britton Falls, Fishers',
    text: 'I\'m a teacher at HSE. My school has group life insurance but it disappears if I change schools. Benjamin helped me get my own policy for $19/month. Total peace of mind.',
    stars: 5,
    product: 'Term Life Insurance',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for a Fishers family?',
    a: 'Most healthy Fishers adults aged 30–40 pay $22–$45/month for $500,000 in 20-year term coverage. A 35-year-old in good health typically pays around $28/month. Because Fishers families tend to have higher incomes and larger mortgages, we usually recommend $750K–$1.5M in coverage — but the math still works out to less than a dinner out.',
  },
  {
    q: 'I work at Roche, Navient, or another major Fishers employer. Isn\'t my group life insurance enough?',
    a: 'Almost never. Employer-provided coverage is usually 1–2x your salary — often capped at $50,000–$100,000. That won\'t cover your Fishers mortgage, replace your income for your family, or fund your kids\' college. Worse, you lose it the moment you change jobs. A private policy stays with you forever, regardless of where you work.',
  },
  {
    q: 'How quickly can I get coverage in Fishers?',
    a: 'Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies are common and can be in force within 24–48 hours. Traditional underwritten policies take 2–4 weeks. We\'ll recommend the fastest path based on your health and coverage amount.',
  },
  {
    q: 'Do I need to come to an office, or can we do this remotely?',
    a: 'Completely remote — phone, Zoom, or Google Meet. Most Fishers clients complete everything in one 20-minute call during a lunch break or after the kids are in bed. We\'ve never required an in-office visit.',
  },
  {
    q: 'I bought a new home in Fishers — what coverage do I need?',
    a: 'At minimum, enough to pay off your mortgage if you pass away. On top of that, you\'ll want income replacement for your spouse — roughly 10x your annual salary. For a Fishers family with a $375,000 mortgage and $95,000 household income, we typically recommend a $750,000–$1,000,000 policy. The monthly cost is usually less than most people expect.',
  },
  {
    q: 'What carriers do you work with for Fishers residents?',
    a: 'We\'re 100% independent and work with 20+ A-rated carriers including Mutual of Omaha, Protective Life, North American Company, Transamerica, Foresters Financial, Americo, and more. We shop your profile across all of them and bring you the best rate — not the best commission.',
  },
];

const NEIGHBORHOODS = [
  'Saxony', 'Geist Reservoir Area', 'Nickel Plate District', 'Sunblest', 'Bristols',
  'Britton Falls', 'Windermere', 'Hoosier Woods', 'Stony Creek', 'Lantern Road',
  '116th St Corridor', 'Fall Creek Area', 'Southeastern Fishers', 'Olio Road Area',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage for the lowest cost. Most Fishers families ages 25–45 choose a 20 or 30-year term to cover their mortgage and income.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Fishers homes average $350,000+. This policy pays your mortgage off completely if something happens to you — no forced sale, ever.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Builds real cash value year after year. Popular with Fishers professionals who\'ve maxed their 401(k) and want a tax-advantaged alternative.',
    cta: 'Explore Whole Life',
    link: '/whole-life',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Get approved in 48 hours with zero needles. Perfect for busy HSE parents and tech professionals who don\'t have time for a full medical exam.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

export default function FishersLifeInsurancePage() {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', zip: '', insuranceType: '', bestTime: '' });
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Life Insurance Fishers IN | Compare 20+ Carriers — Hoosier Life Insurance';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', 'Fishers, IN life insurance — independent agent compares 20+ carriers to find your lowest rate. Most Fishers families save 23% vs. going direct. Free, 15-minute call. (317) 648-2983.');
    setMeta('keywords', 'life insurance Fishers Indiana, Fishers IN life insurance agent, term life insurance Fishers, mortgage protection Fishers, HSE Fishers life insurance, Hamilton County life insurance');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Fishers, Indiana');
    setMeta('geo.position', '39.9568;-86.0139');
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://hoosierlifeinsurance.com/fishers-life-insurance';

    // FAQPage + PriceSpecification schema for AI/voice search
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-fishers#faq',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much is life insurance for a 35-year-old in Fishers, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A healthy 35-year-old in Fishers, Indiana pays approximately $28–$32 per month for $500,000 in 20-year term life coverage. Males typically pay around $32/month and females around $27/month with preferred health ratings. Indiana rates run 8–12% below the national average. An independent agent comparing 20+ carriers will find your lowest available rate.',
          },
        },
        {
          '@type': 'Question',
          name: 'What does a $1 million life insurance policy cost in Fishers, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A $1,000,000 20-year term policy for a healthy 35-year-old in Fishers, Indiana costs approximately $45–$55 per month. For a 40-year-old, expect $65–$80/month. Fishers families with mortgages of $350,000+ and household incomes above $95,000 often choose $750K–$1.5M in coverage to protect both their home and their family\'s income.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much life insurance does a Fishers family with a Roche or Navient group plan need?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Fishers employer group plans cover only 1–2x your salary, which is typically $60,000–$150,000 — far less than the $750,000–$1,500,000 most Fishers families need to cover their mortgage, replace income, and fund college. An independent policy is portable, permanent, and covers the full gap for as little as $30–$50/month.',
          },
        },
        {
          '@type': 'Question',
          name: 'How quickly can I get life insurance coverage in Fishers, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many carriers offer same-day or next-day approval for healthy Fishers applicants. No-medical-exam policies can be active within 24–48 hours. Traditional fully underwritten policies take 2–4 weeks. Most Fishers families complete the entire process in a single 15–20 minute phone or Zoom call.',
          },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-fishers#speakable',
      speakable: {
        '@type': 'SpeakableSpecification',
        xpath: ['/html/body//*[@id="fishers-price-summary"]'],
      },
      url: 'https://hoosierlifeinsurance.com/life-insurance-fishers',
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': 'https://hoosierlifeinsurance.com/fishers-life-insurance#localbusiness',
      name: 'Hoosier Life Insurance — Fishers Indiana',
      url: 'https://hoosierlifeinsurance.com/fishers-life-insurance',
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.9568,
        longitude: -86.0139,
      },
      areaServed: {
        '@type': 'City',
        name: 'Fishers',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving Fishers, Indiana. Roche Diagnostics, Navient, HSE Schools employees, and all Hamilton County families.',
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '127', bestRating: '5', worstRating: '1' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://hoosierlifeinsurance.com/fishers-life-insurance#breadcrumb',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hoosierlifeinsurance.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://hoosierlifeinsurance.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Fishers Indiana', item: 'https://hoosierlifeinsurance.com/fishers-life-insurance' },
      ],
    };

    const schemasToInject = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemasToInject.forEach((schema, idx) => {
      const id = `schema-fishers-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemasToInject.forEach((_, idx) => {
        document.getElementById(`schema-fishers-${idx}`)?.remove();
      });
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append('sms_consent', smsConsent ? 'Yes' : 'No');
      body.append('source_page', 'Fishers Life Insurance Landing Page');
      const res = await fetch('https://readdy.ai/api/form/d6q5mgcddmmni7ck8cb0', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please call us at (317) 648-2983.');
      }
    } catch {
      setError('Something went wrong. Please call us at (317) 648-2983.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <title>Life Insurance Fishers IN | Compare 20+ Carriers — Hoosier Life Insurance</title>
      <meta name="description" content="Fishers life insurance from an independent agent — HSE families, Roche & Salesforce employees save an average 23% by comparing 20+ carriers. Free 15-min consultation. (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/fishers-life-insurance" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance Fishers IN | Compare 20+ Carriers — Hoosier Life Insurance" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/fishers-life-insurance" />
      <meta name="geo.placename" content="Fishers, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="39.9568;-86.0139" />
      <Header />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Fishers, Indiana — Hamilton County</span>
              </div>

              <h1 className="text-[36px] sm:text-[46px] md:text-[54px] font-bold text-white leading-[1.1] mb-4">
                Fishers Families Pay<br />
                <span className="text-[#D97642]">23% Too Much</span><br />
                for Life Insurance.
              </h1>

              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                Because they go straight to one company instead of comparing 20+. We fix that — free, no pressure, in 15 minutes.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent agent right here in Central Indiana. I don&apos;t work for any one carrier. I work for you.
              </p>

              {/* Urgency / trust strip */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: '15-Minute Free Call' },
                  { icon: 'ri-lock-line', text: 'No Pressure, Ever' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`}></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#fishers-quote-form"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line text-[18px]"></i>
                  See My Fishers Rate
                </a>
                <a
                  href="tel:+13176482983"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-[18px]"></i>
                  (317) 648-2983
                </a>
              </div>
            </div>

            {/* Right — stats + image */}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '#1', label: 'Best Place to Live in America', sub: 'Money Magazine — Fishers, IN' },
                  { value: '$95K+', label: 'Median Household Income', sub: 'Hamilton County average' },
                  { value: '$350K+', label: 'Avg Fishers Home Value', sub: 'And rising every year' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You get the best rate available' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/7fcbbebe197cebea0b643dada44ddcf3.jpg"
                alt="Fishers Indiana Family Life Insurance"
                className="w-full h-[220px] object-cover object-top rounded-2xl"
                loading="eager"
                decoding="async"
                width={600}
                height={380}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Proof Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]"></i>
              ))}
            </div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 127 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">500+</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Families Protected</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">$0</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Cost to You</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">15 min</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Average Call Time</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i>
            <span>Indiana Licensed · <strong>Not a Call Center</strong></span>
          </div>
        </div>
      </div>

      {/* ── The Problem Section ── */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">The Problem Most Fishers Families Don&apos;t Know About</span>
              </div>
              <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Your Roche or Navient insurance disappears the day you change jobs.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Most Fishers professionals have life insurance through work. It feels like enough. Until it isn&apos;t.
              </p>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-6">
                Group plans typically only cover 1–2x your salary. Your Fishers mortgage alone is probably $300,000+. Your family needs 10x your income to be truly protected — and group coverage isn&apos;t portable.
              </p>
              <div className="space-y-3">
                {[
                  'Group coverage = 1–2x salary (rarely enough)',
                  'You lose it the moment you leave your employer',
                  'Rates go up every year you wait to get your own policy',
                  'Health changes can disqualify you from future coverage',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Fix — Takes 15 Minutes</span>
              </div>
              <h3 className="text-[26px] font-bold text-white leading-tight mb-4">
                What a private policy does for your Fishers family:
              </h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays off your Fishers mortgage completely', desc: 'Your family stays in their home no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse doesn\'t have to figure it out alone.' },
                { icon: 'ri-graduation-cap-line', title: 'Funds your kids\' college at HSE', desc: 'Their future stays on track, even if yours changes.' },
                { icon: 'ri-lock-line', title: 'Follows you from job to job, forever', desc: 'Roche, Navient, startup — doesn\'t matter. It\'s yours.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`}></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a
                href="#fishers-quote-form"
                className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer"
              >
                Get My Fishers Rate — Free
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Employer Callout Strip ── */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-5">
            We help employees from Fishers&apos; largest employers close the coverage gap
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: 'ri-microscope-line', name: 'Roche Diagnostics' },
              { icon: 'ri-bank-line', name: 'Navient' },
              { icon: 'ri-lightbulb-line', name: 'Launch Fishers Startups' },
              { icon: 'ri-book-open-line', name: 'HSE Schools' },
              { icon: 'ri-building-line', name: 'City of Fishers' },
              { icon: 'ri-heart-pulse-line', name: 'Ascension St. Vincent' },
              { icon: 'ri-shopping-cart-line', name: 'Amazon Fulfillment' },
              { icon: 'ri-settings-line', name: 'Geist Manufacturing' },
            ].map(e => (
              <div key={e.name} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-xl text-[13px] font-semibold text-[#1A3A52]">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${e.icon} text-[#D97642] text-[15px]`}></i></div>
                {e.name}
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-[#718096] mt-4">
            Work somewhere else? We cover <strong>all</strong> Fishers and Hamilton County residents.{' '}
            <a href="#fishers-quote-form" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Get your free rate.</a>
          </p>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Life Insurance for Fishers Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[600px] mx-auto">
              From Saxony to Geist — we help Fishers families find the right coverage at the right price.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <a key={svc.title} href={svc.link} className="group bg-[#F7F9FB] border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-white transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`}></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Does Life Insurance Cost in Fishers? (AI/AEO pricing section) ── */}
      <section id="fishers-price-summary" className="py-14 bg-[#F7F9FB] border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Fishers, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">
              Rates below are for healthy, non-smoking Fishers residents. Indiana rates run 8–12% below the national average. These are actual market rates from the 20+ carriers we compare.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-5 py-3.5 text-left font-semibold">Age</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Coverage</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Term</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Male/Month</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Female/Month</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', coverage: '$500,000', term: '20-year', male: '$28', female: '$23' },
                  { age: '35', coverage: '$500,000', term: '20-year', male: '$32', female: '$27' },
                  { age: '35', coverage: '$1,000,000', term: '20-year', male: '$51', female: '$43' },
                  { age: '40', coverage: '$500,000', term: '20-year', male: '$45', female: '$38' },
                  { age: '40', coverage: '$750,000', term: '20-year', male: '$63', female: '$53' },
                  { age: '45', coverage: '$500,000', term: '20-year', male: '$68', female: '$56' },
                  { age: '50', coverage: '$250,000', term: '20-year', male: '$72', female: '$60' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.coverage}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.male}/mo</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.female}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and individual health profile.</p>
            <a href="#fishers-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]"></i>
              Get My Exact Fishers Rate
            </a>
          </div>
        </div>
      </section>

      {/* ── Quote Form + Testimonials ── */}
      <section id="fishers-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[26px] font-bold text-[#1A3A52] mb-2">What Fishers Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real neighborhoods. No fluff.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]"></i>
                    ))}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All Fishers Neighborhoods</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free Fishers Quote</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]"></i>
                    </div>
                    <h3 className="text-[22px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[15px] text-[#4A5568] mb-6">We&apos;ll review your info and reach out within 24 hours with Fishers-specific options.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form id="fishers-quote-form-el" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="Fishers Life Insurance Landing Page" />

                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Full Name <span className="text-[#D97642]">*</span></label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-gray-400 text-[15px]"></i></div>
                        <input type="text" name="name" required placeholder="Jane Smith" value={formData.name} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-gray-400 text-[15px]"></i></div>
                          <input type="tel" name="phone" required placeholder="(317) 000-0000" value={formData.phone} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-gray-400 text-[15px]"></i></div>
                          <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Fishers ZIP Code <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-gray-400 text-[15px]"></i></div>
                          <input type="text" name="zip" required placeholder="46037" pattern="[0-9]{5}" maxLength={5} value={formData.zip} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">What Are You Looking For?</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-shield-line text-gray-400 text-[15px]"></i></div>
                          <select name="insuranceType" value={formData.insuranceType} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                            <option value="">Help me decide</option>
                            <option value="term_life">Term Life Insurance</option>
                            <option value="whole_life">Whole Life Insurance</option>
                            <option value="mortgage_protection">Mortgage Protection</option>
                            <option value="final_expense">Final Expense Insurance</option>
                            <option value="annuity">Annuity / Retirement</option>
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-gray-400 text-[15px]"></i></div>
                        <select name="bestTime" value={formData.bestTime} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                          <option value="">Any time works</option>
                          <option value="morning">Morning (8am – 12pm)</option>
                          <option value="afternoon">Afternoon (12pm – 5pm)</option>
                          <option value="evening">Evening (5pm – 9pm)</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                      </div>
                    </div>

                    {error && (
                      <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line text-[15px]"></i>
                        {error}
                      </p>
                    )}

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote request. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || !smsConsent}
                      className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-[16px]"></i> Show Me My Fishers Rates — Free</>
                      )}
                    </button>

                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>

              {/* Call option */}
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Neighborhoods ── */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All Fishers Neighborhoods</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Don&apos;t see your neighborhood? We serve all of Hamilton County and surrounding areas. Just give us a call.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-20 bg-[#F7F9FB]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Fishers Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions Fishers families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3">
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[32px] md:text-[40px] font-bold text-white mb-4">
            Fishers deserves better than the default.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            Stop leaving money on the table. 15 minutes, zero pressure, and you&apos;ll know exactly where you stand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#fishers-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]"></i>
              Get My Free Rate Now
            </a>
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]"></i>
              Schedule a Call
            </a>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving Fishers families in 46037, 46038, and 46040</p>
        </div>
      </section>

      {/* ── Indiana City Cross-Link Cluster ── */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Hamilton County</p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {[
              { label: 'Life Insurance Noblesville', href: '/life-insurance-noblesville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Carmel', href: '/life-insurance-carmel', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Hamilton County', href: '/life-insurance-hamilton-county', icon: 'ri-map-2-line' },
            ].map(link => (
              <a key={link.label} href={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </a>
            ))}
          </div>
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Statewide Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
            ].map(link => (
              <a key={link.label} href={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </a>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'Wealth Building with Life Insurance in Indianapolis', href: '/blog/wealth-building-life-insurance-indianapolis' },
                { label: 'Indexed Universal Life Insurance in Indianapolis', href: '/blog/indexed-universal-life-indianapolis' },
                { label: 'Life Insurance for the Self-Employed in Indiana', href: '/blog/life-insurance-self-employed-indiana' },
                { label: 'Term vs Whole Life Insurance in Indianapolis', href: '/blog/term-vs-whole-life-indianapolis' },
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
              ].map(blog => (
                <a key={blog.href} href={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]"></i></div>
                  {blog.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
